import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonButton,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonAlert,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
} from '@ionic/react';
import {
  notificationsOutline,
  mailOutline,
  bookmarkOutline,
  calendarOutline,
  documentTextOutline,
  shareOutline,
  personAddOutline,
  checkmarkOutline,
  closeOutline,
  trashOutline,
  peopleOutline,
  alertCircleOutline,
  ellipsisHorizontalOutline,
  megaphoneOutline,
  chatbubbleEllipsesOutline,
  chatbubbleOutline,
  newspaperOutline,
  chevronForwardOutline,
} from 'ionicons/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../lib/api';
import {
  getProNotificationDestination,
  getProNotificationLink,
} from '../../utils/proNotificationLinks';
import {
  isUpdateNotification,
  isAnnouncementNotification,
  NotificationTabKey,
  DEFAULT_NOTIFICATION_TAB,
} from '../../utils/notificationTabs';
import { useProToolbarEnd } from '../../components/pro/ProToolbarSlot';
import ProPageModal from '../../components/pro/bottom-sheet/ProPageModal';
import { ProLoadingList, ProSegmentTabs } from '../../components/pro/primitives';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

interface PendingInvitation {
  id: string;
  workspace_id: string;
  workspace_name: string;
  workspace_description?: string;
  organization_name: string;
  invited_by_name: string;
  invited_by_username: string;
  role: string;
  created_at: string;
  invitation_type: 'member_invitation' | 'email_invitation';
  expires_at?: string;
}

interface NotificationItem {
  id: string;
  notification_type: string;
  title: string;
  message?: string;
  entity_type?: string;
  entity_id?: string;
  workspace_id?: string;
  actor_id?: string;
  is_read: boolean;
  read_at?: string | null;
  created_at: string;
  is_new_since_last_login: boolean;
  metadata?: Record<string, unknown> | null;
}

interface NotificationsWidgetData {
  invitations: PendingInvitation[];
  notifications: NotificationItem[];
  unreadCount: number;
  newSinceLastLoginCount: number;
}

type TabKey = NotificationTabKey;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const QUERY_KEY = 'pro-notifications';
const NOTIFICATIONS_PAGE_SIZE = 25;

const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const getNotificationIcon = (type: string): string => {
  switch (type) {
    case 'bill_added_to_workspace': return bookmarkOutline;
    case 'calendar_event_created': return calendarOutline;
    case 'note_created': return documentTextOutline;
    case 'note_shared': return shareOutline;
    case 'workspace_invitation': return personAddOutline;
    case 'system_announcement': return megaphoneOutline;
    case 'outreach_response_requested': return chatbubbleEllipsesOutline;
    case 'outreach_recipient_responded': return chatbubbleOutline;
    case 'outreach_update': return newspaperOutline;
    default: return notificationsOutline;
  }
};

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

const ProNotificationsPage: React.FC = () => {
  const { t } = useTranslation('pro');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>(DEFAULT_NOTIFICATION_TAB);
  const [additionalNotifications, setAdditionalNotifications] = useState<NotificationItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [showClearAlert, setShowClearAlert] = useState(false);
  const [showActions, setShowActions] = useState(false);

  // ── Data fetching ──────────────────────────────────────────

  const { data, isLoading, isError, refetch } = useQuery<NotificationsWidgetData>(
    [QUERY_KEY],
    async () => {
      const response = await api.get('/dashboard/widgets/notifications/data');
      return response.data?.data?.data || response.data?.data || response.data;
    },
    { staleTime: 30000, refetchInterval: 60000 },
  );

  const invitations = data?.invitations || [];

  const notifications = useMemo(() => {
    const initial = data?.notifications || [];
    const seen = new Set<string>();
    const combined: NotificationItem[] = [];
    for (const n of initial) {
      if (!seen.has(n.id)) { seen.add(n.id); combined.push(n); }
    }
    for (const n of additionalNotifications) {
      if (!seen.has(n.id)) { seen.add(n.id); combined.push(n); }
    }
    return combined;
  }, [data?.notifications, additionalNotifications]);

  useEffect(() => {
    setAdditionalNotifications([]);
    setHasMore(true);
  }, [data?.notifications?.length]);

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [notifications]);

  const sortedAnnouncements = useMemo(
    () => sortedNotifications.filter(isAnnouncementNotification),
    [sortedNotifications],
  );

  const sortedUpdates = useMemo(
    () => sortedNotifications.filter(isUpdateNotification),
    [sortedNotifications],
  );

  const handleRefresh = async (e: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    e.detail.complete();
  };

  // ── Infinite scroll ────────────────────────────────────────

  const loadMoreNotifications = useCallback(
    async (event: CustomEvent) => {
      const target = (event as any).target as HTMLIonInfiniteScrollElement;
      try {
        if (!hasMore) return;
        const offset = notifications.length;
        const response = await api.get(
          `/notifications?limit=${NOTIFICATIONS_PAGE_SIZE}&offset=${offset}`,
        );
        const newNotifications = response.data?.data?.notifications || [];
        if (newNotifications.length < NOTIFICATIONS_PAGE_SIZE) setHasMore(false);
        if (newNotifications.length > 0) {
          setAdditionalNotifications((prev) => [...prev, ...newNotifications]);
        }
      } catch (error) {
        console.error('Failed to load more notifications:', error);
      } finally {
        target.complete();
      }
    },
    [hasMore, notifications.length],
  );

  // ── Mutations ──────────────────────────────────────────────

  const invalidateNotificationCaches = () => {
    queryClient.invalidateQueries([QUERY_KEY]);
    queryClient.invalidateQueries(['widget', 'notifications']);
    queryClient.invalidateQueries(['notifications']);
    queryClient.invalidateQueries(['notifications-unread-count']);
  };

  const clearAllNotifications = useMutation(() => api.delete('/notifications'), {
    onSuccess: () => {
      toast.success('All notifications cleared');
      setAdditionalNotifications([]);
      setHasMore(false);
      invalidateNotificationCaches();
    },
    onError: () => { toast.error('Failed to clear notifications'); },
  });

  const markRead = useMutation(
    (id: string) => api.put(`/notifications/${id}/read`),
    { onSuccess: () => invalidateNotificationCaches() },
  );

  const markAllRead = useMutation(() => api.put('/notifications/read-all'), {
    onSuccess: () => { toast.success('All marked as read'); invalidateNotificationCaches(); },
    onError: () => { toast.error('Failed to mark all as read'); },
  });

  const acceptMemberInvitation = useMutation(
    (id: string) => api.post(`/workspaces/invitations/member/${id}/accept`),
    {
      onSuccess: () => {
        toast.success('Invitation accepted!');
        invalidateNotificationCaches();
        queryClient.invalidateQueries(['widget-data', 'pending_invitations']);
        queryClient.invalidateQueries(['user-workspaces']);
      },
      onError: () => { toast.error('Failed to accept invitation'); },
    },
  );

  const declineMemberInvitation = useMutation(
    (id: string) => api.post(`/workspaces/invitations/member/${id}/decline`),
    {
      onSuccess: () => {
        toast.success('Invitation declined');
        invalidateNotificationCaches();
        queryClient.invalidateQueries(['widget-data', 'pending_invitations']);
      },
      onError: () => { toast.error('Failed to decline invitation'); },
    },
  );

  const acceptEmailInvitation = useMutation(
    (id: string) => api.post(`/workspaces/invitations/${id}/accept`),
    {
      onSuccess: () => {
        toast.success('Invitation accepted!');
        invalidateNotificationCaches();
        queryClient.invalidateQueries(['widget-data', 'pending_invitations']);
        queryClient.invalidateQueries(['user-workspaces']);
      },
      onError: () => { toast.error('Failed to accept invitation'); },
    },
  );

  const declineEmailInvitation = useMutation(
    (id: string) => api.post(`/workspaces/invitations/${id}/decline`),
    {
      onSuccess: () => {
        toast.success('Invitation declined');
        invalidateNotificationCaches();
        queryClient.invalidateQueries(['widget-data', 'pending_invitations']);
      },
      onError: () => { toast.error('Failed to decline invitation'); },
    },
  );

  // ── Handlers ───────────────────────────────────────────────

  const handleAccept = (inv: PendingInvitation) => {
    if (inv.invitation_type === 'member_invitation') acceptMemberInvitation.mutate(inv.id);
    else acceptEmailInvitation.mutate(inv.id);
  };

  const handleDecline = (inv: PendingInvitation) => {
    if (inv.invitation_type === 'member_invitation') declineMemberInvitation.mutate(inv.id);
    else declineEmailInvitation.mutate(inv.id);
  };

  const handleNotificationClick = (n: NotificationItem) => {
    if (!n.is_read) markRead.mutate(n.id);
    const link = getProNotificationLink(n);
    if (link) navigate(link);
  };

  useProToolbarEnd(
    (
      <IonButton
        fill="clear"
        size="small"
        onClick={() => setShowActions(true)}
        className="text-white"
        aria-label={t('notifications.actions')}
        aria-haspopup="dialog"
        aria-expanded={showActions}
      >
        <IonIcon icon={ellipsisHorizontalOutline} slot="icon-only" />
      </IonButton>
    ),
    [showActions, t],
  );

  // ── Render ─────────────────────────────────────────────────

  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      <div className="max-w-3xl mx-auto">
        <div className="px-4 pt-4 pb-2">
          <p className="text-xs text-text-secondary dark:text-text-dark-secondary">
            {data?.newSinceLastLoginCount
              ? t('notifications.newSinceLogin', { count: data.newSinceLastLoginCount })
              : t('notifications.noNewSinceLogin')}
          </p>
        </div>

        <div className="px-4 pb-3">
          <ProSegmentTabs
            value={activeTab}
            onChange={(v) => setActiveTab(v as TabKey)}
            options={[
              { value: 'updates', label: `Updates (${sortedUpdates.length})` },
              { value: 'invites', label: `Invites (${invitations.length})` },
              { value: 'announcements', label: `Announcements (${sortedAnnouncements.length})` },
            ]}
          />
        </div>

        {/* Content */}
        {isLoading ? (
          <ProLoadingList variant="notification" count={5} className="px-4 py-4 space-y-3" />
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <IonIcon icon={alertCircleOutline} className="text-4xl text-error-500 mb-3" />
            <p className="text-sm text-error-600 dark:text-error-400 mb-2">
              Unable to load notifications.
            </p>
            <IonButton fill="clear" size="small" onClick={() => refetch()}>
              Try again
            </IonButton>
          </div>
        ) : (
          <>
            {/* ── Announcements Tab ── */}
            {activeTab === 'announcements' && (
              <>
                {sortedAnnouncements.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <IonIcon
                      icon={megaphoneOutline}
                      className="text-5xl text-text-tertiary dark:text-text-dark-tertiary mb-3 opacity-50"
                    />
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                      No announcements from Capitol Commons yet.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border-light dark:divide-border-dark">
                    {sortedAnnouncements.map((n) => {
                      const icon = getNotificationIcon(n.notification_type);

                      return (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => handleNotificationClick(n)}
                          className="w-full text-left px-4 py-3.5 flex items-start gap-3 transition-colors active:bg-background-tertiary dark:active:bg-background-dark-tertiary cursor-default"
                        >
                          <div className="mt-0.5 flex-shrink-0">
                            <IonIcon
                              icon={icon}
                              className={`text-xl ${
                                n.is_read
                                  ? 'text-text-tertiary dark:text-text-dark-tertiary'
                                  : 'text-primary-600 dark:text-primary-400'
                              }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span
                                className={`text-sm ${
                                  n.is_read
                                    ? 'text-text-secondary dark:text-text-dark-secondary'
                                    : 'font-medium text-text-primary dark:text-text-dark-primary'
                                }`}
                              >
                                {n.title}
                              </span>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-[11px] text-text-tertiary dark:text-text-dark-tertiary whitespace-nowrap">
                                  {formatRelativeDate(n.created_at)}
                                </span>
                                {!n.is_read && (
                                  <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" />
                                )}
                              </div>
                            </div>
                            {n.message && (
                              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-0.5 whitespace-pre-wrap line-clamp-4">
                                {n.message}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}

                    {!hasMore && sortedAnnouncements.length > 0 && (
                      <div className="text-center py-6 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        No more announcements
                      </div>
                    )}
                  </div>
                )}

                <IonInfiniteScroll
                  disabled={!hasMore || sortedAnnouncements.length === 0}
                  threshold="100px"
                  onIonInfinite={loadMoreNotifications}
                >
                  <IonInfiniteScrollContent loadingText="Loading more..." />
                </IonInfiniteScroll>
              </>
            )}

            {/* ── Updates Tab ── */}
            {activeTab === 'updates' && (
              <>
                {sortedUpdates.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <IonIcon
                      icon={notificationsOutline}
                      className="text-5xl text-text-tertiary dark:text-text-dark-tertiary mb-3 opacity-50"
                    />
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                      You&apos;re all caught up!
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border-light dark:divide-border-dark">
                    {sortedUpdates.map((n) => {
                      const icon = getNotificationIcon(n.notification_type);
                      const destination = getProNotificationDestination(n);
                      const link = destination.href;
                      const showChevron = destination.kind === 'link';

                      return (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => handleNotificationClick(n)}
                          className={`w-full text-left px-4 py-3.5 flex items-start gap-3 transition-colors active:bg-background-tertiary dark:active:bg-background-dark-tertiary ${
                            link ? 'cursor-pointer' : 'cursor-default'
                          }`}
                        >
                          <div className="mt-0.5 flex-shrink-0">
                            <IonIcon
                              icon={icon}
                              className={`text-xl ${
                                n.is_read
                                  ? 'text-text-tertiary dark:text-text-dark-tertiary'
                                  : 'text-primary-600 dark:text-primary-400'
                              }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span
                                className={`text-sm ${
                                  n.is_read
                                    ? 'text-text-secondary dark:text-text-dark-secondary'
                                    : 'font-medium text-text-primary dark:text-text-dark-primary'
                                }`}
                              >
                                {n.title}
                              </span>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-[11px] text-text-tertiary dark:text-text-dark-tertiary whitespace-nowrap">
                                  {formatRelativeDate(n.created_at)}
                                </span>
                                {!n.is_read && (
                                  <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" />
                                )}
                                {showChevron && (
                                  <IonIcon
                                    icon={chevronForwardOutline}
                                    className="text-base text-text-tertiary dark:text-text-dark-tertiary"
                                    aria-hidden="true"
                                  />
                                )}
                              </div>
                            </div>
                            {n.message && (
                              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-0.5 line-clamp-2">
                                {n.message}
                              </p>
                            )}
                            {destination.kind === 'unsupported' && destination.label && (
                              <p className="text-[11px] text-text-tertiary dark:text-text-dark-tertiary mt-1 italic">
                                {destination.label}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}

                    {!hasMore && sortedUpdates.length > 0 && (
                      <div className="text-center py-6 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        No more notifications
                      </div>
                    )}
                  </div>
                )}

                <IonInfiniteScroll
                  disabled={!hasMore || sortedUpdates.length === 0}
                  threshold="100px"
                  onIonInfinite={loadMoreNotifications}
                >
                  <IonInfiniteScrollContent loadingText="Loading more..." />
                </IonInfiniteScroll>
              </>
            )}

            {/* ── Invites Tab ── */}
            {activeTab === 'invites' && (
              <>
                {invitations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <IonIcon
                      icon={mailOutline}
                      className="text-5xl text-text-tertiary dark:text-text-dark-tertiary mb-3 opacity-50"
                    />
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                      No pending invitations
                    </p>
                  </div>
                ) : (
                  <div className="px-4 space-y-3 py-2">
                    {invitations.map((invitation) => (
                      <div
                        key={`${invitation.invitation_type}-${invitation.id}`}
                        className="border border-border-light dark:border-border-dark rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="mt-0.5 flex-shrink-0">
                              <IonIcon
                                icon={
                                  invitation.invitation_type === 'member_invitation'
                                    ? peopleOutline
                                    : mailOutline
                                }
                                className="text-xl text-primary-600 dark:text-primary-400"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm text-text-primary dark:text-text-dark-primary">
                                {invitation.workspace_name}
                              </h4>
                              <p className="text-xs text-text-secondary dark:text-text-dark-secondary">
                                {invitation.organization_name}
                              </p>
                              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-0.5">
                                Invited by{' '}
                                {invitation.invited_by_name || `@${invitation.invited_by_username}`}
                              </p>
                            </div>
                          </div>
                          <span className="text-xs px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full whitespace-nowrap ml-2">
                            {invitation.role}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                            {formatRelativeDate(invitation.created_at)}
                          </span>
                          <div className="flex items-center gap-2">
                            <IonButton
                              fill="outline"
                              size="small"
                              color="medium"
                              onClick={() => handleDecline(invitation)}
                              disabled={
                                declineMemberInvitation.isLoading ||
                                declineEmailInvitation.isLoading
                              }
                            >
                              <IonIcon icon={closeOutline} slot="start" />
                              Decline
                            </IonButton>
                            <IonButton
                              fill="solid"
                              size="small"
                              color="primary"
                              onClick={() => handleAccept(invitation)}
                              disabled={
                                acceptMemberInvitation.isLoading ||
                                acceptEmailInvitation.isLoading
                              }
                            >
                              <IonIcon icon={checkmarkOutline} slot="start" />
                              Accept
                            </IonButton>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Notification bulk actions — ProPageModal avoids IonHeader clipping the old dropdown. */}
      <ProPageModal
        isOpen={showActions}
        onDismiss={() => setShowActions(false)}
        title={t('notifications.actions')}
      >
        <div className="px-4 py-3 space-y-2">
          <button
            type="button"
            onClick={() => { markAllRead.mutate(); setShowActions(false); }}
            disabled={markAllRead.isLoading || (data?.unreadCount || 0) === 0}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-700 text-white font-semibold text-sm transition-colors active:bg-primary-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <IonIcon icon={checkmarkOutline} className="text-lg" />
            {t('notifications.markAllRead')}
          </button>
          <button
            type="button"
            onClick={() => { setShowClearAlert(true); setShowActions(false); }}
            disabled={clearAllNotifications.isLoading || sortedNotifications.length === 0}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-red-300 dark:border-red-400/40 bg-transparent text-red-600 dark:text-red-400 font-semibold text-sm transition-colors active:bg-red-50 dark:active:bg-red-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <IonIcon icon={trashOutline} className="text-lg" />
            {t('notifications.clearAll')}
          </button>
        </div>
      </ProPageModal>

      {/* Clear all confirmation */}
      <IonAlert
        isOpen={showClearAlert}
        onDidDismiss={() => setShowClearAlert(false)}
        header="Clear all notifications"
        message="This cannot be undone. Are you sure?"
        buttons={[
          { text: 'Cancel', role: 'cancel' },
          {
            text: 'Clear all',
            role: 'destructive',
            handler: () => clearAllNotifications.mutate(),
          },
        ]}
      />
    </>
  );
};

export default ProNotificationsPage;
